import { useEffect } from 'react';

import type { Core } from '@pdftron/webviewer';

import { getSignatureFieldBox } from './signature-field-navigation';
import type { DetectedSignatureField } from './signature-field-navigation';

/**
 * Draws a box over the selected detected signature so the user can see what
 * detection is pointing at.
 *
 * This is a plain DOM element appended to WebViewer's per-page container, not
 * an annotation. That is deliberate and load-bearing: `syncAnnotations`
 * exports every annotation in the manager to XFDF and uploads it, so a
 * highlight added there would be written into the user's document and kept
 * forever. Nothing here reaches the annotation manager.
 *
 * Page coordinates are PDF points with a top-left origin and the page
 * container scales by the zoom level, which is why every value is multiplied
 * by it. The box is repositioned on zoom and on page render rather than
 * recreated.
 */
export function useSignatureFieldHighlight(
  documentViewer: Core.DocumentViewer | null,
  field: DetectedSignatureField | null
): void {
  useEffect(() => {
    if (!documentViewer || !field) return;

    const box = getSignatureFieldBox(field);
    if (!box) return;

    const element = document.createElement('div');
    element.dataset.alineSignatureHighlight = 'true';
    Object.assign(element.style, {
      backgroundColor: 'rgba(37, 99, 235, 0.12)',
      border: '2px solid rgba(37, 99, 235, 0.9)',
      borderRadius: '2px',
      boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.08)',
      pointerEvents: 'none',
      position: 'absolute',
      transition: 'opacity 120ms ease-out',
      zIndex: '1',
    });

    const position = () => {
      const container = getPageContainer(documentViewer, box.pageNumber);
      if (!container) return;
      if (element.parentElement !== container) container.append(element);

      const zoom = documentViewer.getZoomLevel();
      Object.assign(element.style, {
        height: `${box.height * zoom}px`,
        left: `${box.x * zoom}px`,
        top: `${box.y * zoom}px`,
        width: `${box.width * zoom}px`,
      });
    };

    position();
    documentViewer.addEventListener('zoomUpdated', position);
    documentViewer.addEventListener('pageComplete', position);

    return () => {
      documentViewer.removeEventListener('zoomUpdated', position);
      documentViewer.removeEventListener('pageComplete', position);
      element.remove();
    };
  }, [documentViewer, field]);
}

/**
 * WebViewer renders one absolutely-positioned container per page and keeps it
 * aligned with the page as the view scrolls and zooms, so anything appended to
 * it inherits that positioning for free.
 */
function getPageContainer(
  documentViewer: Core.DocumentViewer,
  pageNumber: number
): HTMLElement | null {
  const selector = `#pageWidgetContainer${pageNumber}`;

  return (
    documentViewer.getViewerElement()?.querySelector<HTMLElement>(selector) ??
    documentViewer.getScrollViewElement()?.querySelector<HTMLElement>(selector) ??
    null
  );
}
